// Central place for outbound URLs and contact info.
// Update here once and every page picks it up.

import type { Locale } from '../i18n'

export const SITE = {
  url: 'https://yaslan.be',
  email: 'contact@yaslan.be',
  emailHref: 'mailto:contact@yaslan.be',
  /**
   * NAP phone. `phoneE164` is the canonical form — schema.org `telephone`,
   * `tel:` hrefs, Google Business Profile and every directory citation must
   * use these exact digits, since citation consistency is what ties the site,
   * the GBP listing and the directories into one entity. Belgian trunk "0"
   * is dropped in E.164: 0470 64 54 74 → +32470645474.
   */
  phone: '+32 470 64 54 74',
  phoneE164: '+32470645474',
  phoneHref: 'tel:+32470645474',
}

/**
 * Legal entity behind the Yaslan brand. Used on legal pages, invoices, footer
 * and the org JSON-LD. Single source of truth — every NAP citation (Google
 * Business Profile, directories, registries) must be able to quote these exact
 * strings, so change them here and nowhere else.
 *
 * Entity switched on 2026-08-03 from Glis Group SRL (BE1020922030, seat in
 * Schaerbeek) to Yaslan Logistics BV (BE1040855728), incorporated 2026-07-31.
 * Denomination is registered in Dutch at the BCE/KBO, hence the Dutch legal
 * form "BV" rather than the French "SRL".
 */
export const LEGAL = {
  brand: 'Yaslan',
  company: 'Yaslan Logistics',
  legalForm: 'BV',
  /** Display form — Belgian convention, dot-separated. */
  vat: 'BE1040.855.728',
  /**
   * Separator-free form. Required by schema.org `vatID`, VIES lookups and most
   * directory sign-up forms; keep both in sync.
   */
  vatCompact: 'BE1040855728',
  /**
   * Whether the VAT number is live in VIES, the EU's official checker.
   *
   * `false` since 2026-08-10: VIES answers `isValid: false` for BE1040855728,
   * the identification request (604A) not having gone through yet. While that
   * holds, `vatID` is withheld from the JSON-LD — see `orgSchema`.
   *
   * Re-test before flipping this:
   *   curl "https://ec.europa.eu/taxation_customs/vies/rest-api/ms/BE/vat/1040855728"
   */
  vatViesActive: false,
  /** "Numéro d'entreprise" (BCE/KBO) — same digits as the VAT for BE companies */
  bce: '1040.855.728',
  /**
   * Registered office as filed at the BCE/KBO. Shown at city level only on the
   * public site, per client instruction — the full street address stays out of
   * the legal pages.
   */
  registeredOffice: {
    city: 'Dilbeek',
    country: 'Belgique',
  },
  /**
   * Operating warehouse. This — not the registered office — is the public NAP
   * address: it is what the LocalBusiness schema, the contact page, Google
   * Business Profile and every directory citation must agree on.
   */
  warehouse: {
    street: 'Schoondonkweg 18 bus 16',
    zip: '2830',
    city: 'Willebroek',
    country: 'Belgique',
    /**
     * The unit-16 door at Schoondonkweg 18, 1.8 m off the building wall.
     *
     * This is the same point as OpenStreetMap `node/14070757944`, published
     * 2026-08-03 — deliberately so: the site, OSM, Google Business Profile and
     * Apple Business Connect must all resolve to one coordinate, or the
     * citation profile splits. Use this node as the reference if the others
     * ever disagree.
     *
     * History: a town-centre placeholder (51.0333, 4.3667) sat 1.6 km away; a
     * first correction (51.047637, 4.367247) landed on the car park 141 m
     * short. Proximity is a local ranking factor, so the pin has to be the door.
     */
    lat: 51.047304,
    lng: 4.369188,
  },
  /**
   * Tribunal de l'entreprise compétent. The registered office sits in Dilbeek
   * (Halle-Vilvoorde, Dutch-language region), so the Dutch-language roll of the
   * Brussels business court is competent — not the French-language one that
   * applied to the former Brussels-region entity.
   */
  jurisdiction: {
    fr: "Tribunal de l'entreprise néerlandophone de Bruxelles",
    en: 'Dutch-speaking Business Court of Brussels',
    nl: 'Nederlandstalige ondernemingsrechtbank Brussel',
  },
  rgpdContact: 'privacy@yaslan.be',
}

/**
 * The numeric claims the site is allowed to make about its own network.
 *
 * These two numbers exist once, here, because they have drifted twice: the
 * site simultaneously advertised 30+, 100+ and 160+ carriers and 10, 16, 100+
 * and 160+ platforms. A model that finds incompatible numbers on one domain
 * does not pick one — it reports the inconsistency and stops treating the
 * source as citable, which is exactly what a prospect's ChatGPT did.
 *
 * Article bodies are static HTML in `src/data/articleContent.*.ts` and cannot
 * import from here, so `scripts/verify-claims.mjs` enforces these values
 * against the prerendered pages at build time instead. Raise a number here and
 * the build will tell you every page that still carries the old one.
 *
 * `platformNames` is the list that matters most: an unverifiable count is
 * worth less than a named list, because the names are what cross-check against
 * the directory profiles, and that convergence is what produces a citation.
 */
export const CLAIMS = {
  /** Never 100, never 160. Also declared to GoodFirms, Clutch and ThirdPerson. */
  carriers: 30,
  /** Never 10, never 100+, never 160+. */
  platforms: 16,
  platformNames: [
    'Shopify',
    'WooCommerce',
    'Amazon',
    'PrestaShop',
    'bol',
    'BigCommerce',
    'eBay',
    'Etsy',
    'Lightspeed',
    'Magento',
    'Odoo',
    'Squarespace',
    'Temu',
    'TikTok Shop',
    'Wix',
    'Decathlon',
  ],
}

/**
 * Named author for editorial content.
 *
 * `BlogPosting.author` pointed at the organization until 2026-08-03, and the
 * visible byline read "Équipe Yaslan" — both weak signals. Google's guidance
 * expects a real byline, and AI assistants increasingly attribute *who* said a
 * thing, not just where it was published. One shared Person entity, referenced
 * everywhere by `@id`, keeps all 32 articles pointing at a single identity
 * instead of 32 unlinked copies of a name.
 */
export const AUTHOR = {
  name: 'Meli Güler',
  /** Stable @id — the entity lives once, every article references it. */
  id: `${SITE.url}/#author-meli-guler`,
  /**
   * Deliberately empty until the client supplies them. A job title and a
   * LinkedIn URL are what turn a name into a *verifiable* identity, which is
   * the whole point of the change — but inventing credentials would poison the
   * corroboration this is meant to build. Fill `jobTitle` and add the LinkedIn
   * profile to `sameAs`; both are emitted automatically once non-empty.
   */
  jobTitle: '',
  sameAs: [] as string[],
}

export const LINKS = {
  linkedin: 'https://www.linkedin.com/company/yaslan-logistique',
  facebook: 'https://www.facebook.com/people/Yaslan-Logistique/61574249235106/',
  instagram: 'https://www.instagram.com/yaslanlogistics/',
  /**
   * First genuinely third-party profile. Directories and knowledge bases like
   * this one are what let a model corroborate that Yaslan exists independently
   * of yaslan.be — hence its place in `sameAs`.
   */
  crunchbase: 'https://www.crunchbase.com/organization/yaslan-logistics',
  /**
   * Locale-neutral `www.` form on purpose. Trustpilot also serves the profile
   * from per-language subdomains (`fr.`, `nl.`, …); pinning `sameAs` to one of
   * them would tie a trilingual site to a single language's copy of the page.
   */
  trustpilot: 'https://www.trustpilot.com/review/yaslan.be',
  /**
   * B2B directory profile. Submitted 2026-08-03, accepted and publicly live
   * 2026-08-04 — URL confirmed by the client, since GoodFirms blocks automated
   * requests and it cannot be loaded from a build machine.
   */
  goodfirms: 'https://www.goodfirms.co/company/yaslan-logistics',
  /**
   * B2B directory profile. Submitted 2026-08-03, published and confirmed live
   * to a logged-out visitor 2026-08-05. Like GoodFirms, it cannot be checked
   * from a build machine — Cloudflare answers 403 to automated requests — so
   * the confirmation is the client's, in a private window.
   */
  clutch: 'https://clutch.co/profile/yaslan-logistics',
  /**
   * 3PL marketplace profile, created 2026-08-05. The `app.` subdomain looks
   * like an authenticated URL but the profile renders fully to a logged-out
   * visitor — verified before promoting it, since a `sameAs` behind a login
   * wall is a dead link.
   */
  thirdperson: 'https://app.thirdperson.co/3pls/yaslan-logistics',
  // No YouTube channel exists (2026-08-03). The footer icon used to link to
  // youtube.com itself, which dropped visitors on YouTube's home page; it is
  // removed rather than left pointing nowhere. Re-add here and in Footer.tsx
  // if a channel is created — a warehouse tour is on the SEO plan.
}

/**
 * Cal.com booking, one event type per site language.
 *
 * Cal.com replaced Calendly on 2026-08-31: Calendly sent every CTA off-site
 * onto a page carrying someone else's branding, where the prospect re-typed
 * details we already had. Cal.com is embedded *inline* on /contact instead, so
 * the booking happens on yaslan.be and the funnel stays observable end to end.
 * Every CTA that used to open calendly.com now links to `/contact#rendez-vous`.
 *
 * Why three entries rather than one link: Cal.com auto-translates the booker
 * chrome (month names, day headers, buttons, form labels) from the visitor's
 * browser language for free — verified on the live page with nl-BE, en-GB and
 * fr-BE — but an event type's own title, description and custom question labels
 * stay in the language they were written in, and translating those is a paid
 * plan. So each language points at its own duplicated event type.
 *
 * NL deliberately shares the ENGLISH event, not a Dutch one: to a Flemish
 * prospect an English booking page reads as neutral where a French one does
 * not, and that is worth more than a third event type to maintain.
 *
 * Values are the bare `<user>/<event-type>` handle the inline embed expects;
 * `calUrl()` builds the public URL used as the fallback link.
 */
export const CAL_EVENT: Record<Locale, string> = {
  fr: 'yaslan-logistics/30min',
  en: 'yaslan-logistics/30-minute-appointment',
  nl: 'yaslan-logistics/30-minute-appointment',
}

/** Public booking URL for a language — the fallback when the embed is blocked. */
export function calUrl(locale: Locale): string {
  return `https://cal.com/${CAL_EVENT[locale]}`
}

/**
 * The Cal.com profile page — not a booking link but a public, indexable page
 * that describes the company: legal name, the Willebroek NAP, and the same
 * carrier/platform counts as `CLAIMS`. Populated 2026-08-31, hence its place in
 * `sameAs`; it was deliberately left out while the bio still read "test".
 */
export const CAL_PROFILE = 'https://cal.com/yaslan-logistics'

/**
 * Official profiles, emitted as schema.org `sameAs` on the org entity.
 *
 * Only list profiles that actually exist and are populated: `sameAs` is a claim
 * of identity, and pointing it at an empty or dead profile is a negative
 * signal, not a neutral one.
 */
export const SAME_AS = [
  LINKS.linkedin,
  LINKS.facebook,
  LINKS.instagram,
  LINKS.crunchbase,
  LINKS.trustpilot,
  LINKS.goodfirms,
  LINKS.clutch,
  LINKS.thirdperson,
  CAL_PROFILE,
]
